import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Blockquote from '@tiptap/extension-blockquote';
import Text from '@tiptap/extension-text';
import HardBreak from '@tiptap/extension-hard-break';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import { lowlight } from 'lowlight';
import ArcscriptIndentation from '@/tip-tap-custom-extensions/arcscript-indentation.js';
import initPasteHandler from '@/tip-tap-custom-extensions/paste-plugin.js';

import { renderLabel } from '@/vuex/project/mention-helper.js';
import Mention from '@/tip-tap-custom-extensions/custom-mention.js';
import Link from '@/tip-tap-custom-extensions/custom-link.js';
import CustomCodeBlockHighlight from '@/tip-tap-custom-extensions/custom-code-block-highlight.js';
import DropEventHandler from '@/tip-tap-custom-extensions/custom-drop-plugin.js';
import Suggestion from '@/tip-tap-custom-extensions/suggestion.js';
import { useEditor } from '@tiptap/vue-3';
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import componentTypes from '@/vuex/project/types/components-types.js';
import Rect from '@/rect.js';
import Point from '@/point.js';
import { throttle } from 'lodash';
import { AllSelection, TextSelection } from 'prosemirror-state';
import { getScripts } from '@/utils.js';

/**
 * Checks whether the content of the editor is empty
 * @param {import('@tiptap/core').Editor} editor
 * @returns {Boolean}
 */
function isContentEmpty(editor) {
  const { dom } = editor.view;
  if (!dom) {
    return true;
  }
  return dom.querySelectorAll('p.is-empty:first-child').length > 0;
}

/**
 * Checks whether the selection of the editor is empty
 * @param {import('@tiptap/core').Editor} editor
 * @returns {Boolean}
 */
function isSelectionEmpty(editor) {
  const { view } = editor;
  const { selection } = view.state;
  return selection.empty;
}

/**
 * Enables or disables the code block based on the content of the editor
 * @param {Object} param0
 * @param {import('@tiptap/core').Editor} param0.editor
 * @param {Object} param0.options
 */
function setCodeBlock({ editor, options }) {
  const { codeBlockHighlight } = options;
  if (!codeBlockHighlight) return;

  if (codeBlockHighlight.toolbarButton) return;

  const content = editor.getHTML();
  const scripts = getScripts(content);
  const hasScript = scripts && scripts.length && scripts[0];
  if (editor.isActive('CustomCodeBlockLowLight') && !hasScript) {
    editor.commands.clearContent();
  } else if (
    !editor.isActive('CustomCodeBlockLowLight') &&
    !isContentEmpty(editor)
  ) {
    editor.commands.toggleCodeBlock();
  }
}

/**
 * Creates the toolbar based on the options
 * @param {Object} param0
 * @param {import('@tiptap/core').Editor} param0.editor
 * @param {Object} param0.options
 * @param {import('vuex').Store} param0.store
 */
function createToolbar({ editor, options, store }) {
  if (options.bold) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 1,
      name: 'bold',
      icon: 'app-icons/bold',
      clickCommand: () => {
        editor.chain().focus().toggleBold().run();
      },
      isActiveCommand: editor.isActive.bind(editor, 'bold'),
      disabled: editor.isActive.bind(editor, 'CustomCodeBlockLowLight'),
    });
  }
  if (options.italic) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 2,
      name: 'italic',
      icon: 'app-icons/italic',
      clickCommand: () => {
        editor.chain().focus().toggleItalic().run();
      },
      isActiveCommand: editor.isActive.bind(editor, 'italic'),
      disabled: editor.isActive.bind(editor, 'CustomCodeBlockLowLight'),
    });
  }
  if (options.underline) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 3,
      name: 'underline',
      icon: 'app-icons/underline',
      clickCommand: () => {
        editor.chain().focus().toggleUnderline().run();
      },
      isActiveCommand: editor.isActive.bind(editor, 'underline'),
      disabled: editor.isActive.bind(editor, 'CustomCodeBlockLowLight'),
    });
  }
  if (options.blockquote) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 4,
      name: 'blockquote',
      icon: 'app-icons/blockquote',
      clickCommand: () => {
        editor.chain().focus().toggleBlockquote().run();
      },
      isActiveCommand: editor.isActive.bind(editor, 'blockquote'),
      disabled: editor.isActive.bind(editor, 'CustomCodeBlockLowLight'),
    });
  }
  if (options.horizontalRule) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 5,
      name: 'horizontal rule',
      icon: 'app-icons/horizontal-rule',
      clickCommand: () => {
        editor.chain().focus().setHorizontalRule().run();
      },
      isActiveCommand: null,
      disabled: editor.isActive.bind(editor, 'CustomCodeBlockLowLight'),
    });
  }
  if (options.customLink) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 6,
      name: 'link',
      icon: 'app-icons/link',
      clickCommand: null,
      isActiveCommand: () => {
        return editor.isActive('link') && !isSelectionEmpty(editor);
      },
      disabled: () => {
        return (
          isSelectionEmpty(editor) || editor.isActive('CustomCodeBlockLowLight')
        );
      },
      submenu: {
        component: 'submenu-link',
        props: {
          getMarkAttrs: editor.getAttributes.bind(editor, 'link'),
          linkCommand: ({ href, target = '_blank' }) => {
            if (href) {
              editor.chain().focus().setLink({ href, target }).run();
            } else {
              editor.chain().focus().unsetLink().run();
            }
          },
          focusCommand: () => {
            editor.commands.focus();
          },
        },
      },
    });
  }
  if (options.codeBlockHighlight && options.codeBlockHighlight.toolbarButton) {
    store.dispatch('application/contextToolbar/addButton', {
      id: 7,
      name: 'arcscript segment',
      icon: 'app-icons/script',
      clickCommand: () => {
        editor.chain().focus().toggleCodeBlock().run();
      },
      isActiveCommand: editor.isActive.bind(editor, 'CustomCodeBlockLowLight'),
    });
  }
}

/**
 * Removes the toolbar
 * @param {Object} param0
 * @param {import('vuex').Store} param0.store
 */
function removeToolbar({ store }) {
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'bold',
  });
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'italic',
  });
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'underline',
  });
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'horizontal rule',
  });
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'link',
  });
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'arcscript segment',
  });
  store.dispatch('application/contextToolbar/removeButton', {
    name: 'blockquote',
  });
}

/**
 * Initializes the default mention suggestions
 * @param {import('vuex').Store} store
 * @returns {Object}
 */
function initDefaultMentions(store) {
  return {
    component: [
      {
        id: 'default-0',
        mention_id: 'default-0',
        mention_icon: 'app-icons/create',
        mention_type: 'component',
        mention_name: query => {
          if (query.length > 0) {
            return `Create "${query.trim().replace(/_/g, ' ')}"`;
          }
          return 'Create Untitled Component';
        },
        handler: async (mention, query) => {
          if (query.trim() === '') {
            query = 'Untitled Component';
          }
          const mentionName = query.replace(/_/g, ' ');
          const id = await store.dispatch(
            'project/data/components/customCreate',
            {
              component: componentTypes.newItem,
              tIndex: '-',
              props: {
                name: mentionName,
              },
              apply: true,
            }
          );

          return Promise.resolve({
            id,
            mention_name: mentionName,
          });
        },
      },
    ],
  };
}

/**
 * Initializes the options with the default values
 * @param {Object} options
 * @returns Object
 */
function initializeOptions(options) {
  return {
    ...{
      bold: false,
      blockquote: false,
      codeBlockHighlight: null,
      customLink: false,
      customMention: false,
      dropEventHandler: false,
      hardbreak: false,
      history: false,
      horizontalRule: false,
      italic: false,
      placeholder: null,
      underline: false,
      toolbarOnSelect: false,
    },
    ...options,
  };
}

/**
 * @typedef {Object} RichEditorResponse
 * @property {import('vue').Ref<import('@tiptap/core').Editor>} editor The editor
 * @property {Function} isContentEmpty Checks whether the content of the editor is empty
 * @property {Function} removeToolbar Removes the toolbar
 */

/**
 *
 * @param {Object} RichEditorOptions
 * @param {Object} RichEditorOptions.options The editor options
 * @param {Object} RichEditorOptions.caret The caret position
 * @param {Function} RichEditorOptions.emit The emit function
 * @param {String} RichEditorOptions.content The content
 * @param {Function} RichEditorOptions.onUpdated The onUpdated
 * @returns {RichEditorResponse}
 */
export function useRichEditor({
  options,
  caret,
  emit,
  content = '',
  onUpdated = () => {},
}) {
  options = initializeOptions(options);

  const store = useStore();

  const getComponentMentions = computed(
    () => store.getters['project/data/components/getMentions']
  );

  const getBoardMentions = computed(
    () => store.getters['project/data/boards/getMentions']
  );

  const getElementMention = computed(
    () => store.getters['project/data/elements/getMention']
  );

  const getElementMentions = computed(
    () => store.getters['project/data/elements/getMentions']
  );

  const getBoard = computed(() => store.getters['project/data/boards/getNode']);

  const openBoard = computed(
    () => store.getters['project/config/boards/getOpen']
  );
  let editor = null;

  const popup = ref(null);

  function getMentions(query) {
    let types = ['component', 'board'];
    const { codeBlockHighlight } = options;
    if (
      editor.value.isActive('CustomCodeBlockLowLight') ||
      codeBlockHighlight?.toolbarButton === false
    ) {
      types = ['element'];
    }

    const mentions = [];
    const sortFn = (a, b) => {
      if (a.mention_name > b.mention_name) {
        return 1;
      }
      if (a.mention_name < b.mention_name) {
        return -1;
      }
      return 0;
    };

    types.forEach(type => {
      const allowMention =
        options.customMention === true || options.customMention.includes(type);

      if (type === 'component' && allowMention) {
        mentions.push({
          type: 'component',
          label: 'COMPONENTS',
          items: getComponentMentions.value.sort(sortFn),
          showIcon: true,
        });
      }
      if (type === 'board' && allowMention) {
        mentions.push({
          type: 'board',
          label: 'BOARDS',
          items: getBoardMentions.value.sort(sortFn),
          showIcon: true,
        });
      }
      if (type === 'element' && allowMention) {
        let elementMentions;
        if (!query) {
          elementMentions = getBoard
            .value(openBoard.value)
            .elements.map(elementId => getElementMention.value(elementId));
        } else {
          elementMentions = getElementMentions.value;
        }
        elementMentions.sort(sortFn);
        mentions.push({
          type: 'element',
          label: 'ELEMENTS',
          items: elementMentions,
          showTheme: true,
          showIcon: false,
        });
      }
    });

    let index = 0;
    mentions.forEach(type => {
      type.items.forEach(mention => {
        mention.index = index;
        index += 1;
      });
    });

    return mentions;
  }

  const extensions = [
    Document,
    Paragraph,
    Text,
    initPasteHandler(store),
    ...(options.hardbreak ? [HardBreak] : []),
    ...(options.bold ? [Bold] : []),
    ...(options.blockquote ? [Blockquote] : []),
    ...(options.italic ? [Italic] : []),
    ...(options.underline ? [Underline] : []),
    ...(options.horizontalRule ? [HorizontalRule] : []),
    ...(options.customLink ? [Link] : []),
    ...(options.history ? [History] : []),
    ...(options.placeholder
      ? [
          Placeholder.configure({
            emptyNodeClass: 'is-empty',
            emptyEditorClass: 'is-editor-empty',
            placeholder: options.placeholder.text,
            showOnlyWhenEditable: false,
            showOnlyCurrent: true,
          }),
        ]
      : []),
    ...(options.dropEventHandler ? [DropEventHandler] : []),
    ...(options.customMention
      ? [
          Mention.configure({
            HTMLAttributes: { class: 'mention' },
            suggestion: Suggestion(
              getMentions,
              popup,
              initDefaultMentions(store)
            ),
            renderLabel,
          }),
        ]
      : []),
    ...(options.codeBlockHighlight
      ? [
          CustomCodeBlockHighlight.configure({
            lowlight,
            defaultLanguage: 'arcscript',
            exitOnArrowDown: false,
            initialMargin: 0,
          }),
          ArcscriptIndentation.configure({
            types: ['paragraph', 'CustomCodeBlockLowLight', 'blockquote'],
          }),
        ]
      : []),
  ];

  editor = useEditor({
    enableInputRules: true, // Allow markdown support
    enablePasteRules: false,
    extensions,
    content,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    onFocus: ({ editor: eventEditor, event }) => {
      if (!options.toolbarOnSelect) {
        createToolbar({ editor: eventEditor, options, store });
      }
      emit('focus', event);
    },
    onBlur: ({ editor: eventEditor, event }) => {
      if (!event.relatedTarget) {
        if (popup.value) {
          popup.value[0].destroy();
          popup.value = null;
        }
      }
      if (!popup.value) {
        const buttons = store.getters['application/contextToolbar/getButtons'];
        if (!buttons.length) {
          emit('blur', eventEditor.getHTML());
        } else if (
          !document
            .querySelector('.floatingtoolbar')
            ?.contains(event.relatedTarget)
        ) {
          // Clicked outside the toolbar
          emit('update', eventEditor.getHTML());
          removeToolbar({ store });
          emit('blur', eventEditor.getHTML());
        }
      }
    },
    onSelectionUpdate: ({ editor: eventEditor }) => {
      if (options.toolbarOnSelect) {
        if (!isSelectionEmpty(eventEditor)) {
          store.dispatch('application/contextToolbar/setAnchor', {
            anchor: () => {
              const inputRect = eventEditor.view.dom.getBoundingClientRect();
              const rect = new Rect(
                inputRect.left,
                inputRect.right,
                inputRect.top,
                inputRect.bottom
              );
              const parentRect = document
                .getElementById('panzoom-parent')
                .getBoundingClientRect();
              const parentPt = new Point(parentRect.left, parentRect.top);
              rect.offsetRect(parentPt);
              return rect;
            },
          });
          createToolbar({ editor: eventEditor, options, store });
        } else {
          removeToolbar({ store });
        }
      }
    },
    onUpdate: throttle(({ editor: eventEditor }) => {
      setCodeBlock({ editor: eventEditor, options });
      onUpdated(eventEditor.getHTML());
      emit('update', eventEditor.getHTML());
      emit('input', eventEditor.getHTML());
    }, 100),
    onCreate: ({ editor: eventEditor }) => {
      setCodeBlock({ editor: eventEditor, options });
      if (caret) {
        const { mouse, select, scrollTop } = caret;
        const { view } = eventEditor;
        const { doc, tr } = view.state;
        if (mouse) {
          if (scrollTop) {
            eventEditor.view.dom.scrollTop = scrollTop;
          }
          const result = view.posAtCoords({ left: mouse.x, top: mouse.y });
          if (result) {
            const { pos } = result;
            const resolvedPos = doc.resolve(pos);
            const selection = TextSelection.near(resolvedPos);
            const transaction = tr.setSelection(selection);
            view.dispatch(transaction);
          }
        } else if (select) {
          if (select === 'start') {
            view.dispatch(tr.setSelection(TextSelection.near(doc.resolve(0))));
          } else if (select === 'end') {
            view.dispatch(
              tr.setSelection(TextSelection.near(doc.resolve(doc.content.size)))
            );
          } else if (select === 'all') {
            const allSelection = new AllSelection(doc);
            view.dispatch(tr.setSelection(allSelection));
          }
        }
      }
      eventEditor.commands.focus();

      emit('focus');
    },
  });

  const isEditorContentEmpty = () => isContentEmpty(editor.value);
  const removeEditorToolbar = () => removeToolbar({ store });
  return {
    editor,
    isContentEmpty: isEditorContentEmpty,
    removeToolbar: removeEditorToolbar,
  };
}
